import { useNavigate } from "react-router";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { TutorialUsage } from "./TutorialUsageByCourseAndFocusViewModel";

interface ChartData {
	name: string;
	value: number;
	link: string;
}

function initializeOptions(data: ChartData[]): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			top: 20,
			left: 30,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Total Time Spent (in minutes)",
			nameLocation: "middle",
			nameGap: 30,
		},
		yAxis: {
			type: "category",
			data: data.map((d) => {
				return d.name;
			}),
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: function (params: { value: string; name: string; color: string }) {
				return (
					params.name +
					"<br />" +
					tooltipMark(params.color) +
					"Time Spent: <b>" +
					params.value +
					"</b> min"
				);
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: {
			type: "bar",
			id: "category",
			data: data.map((d) => ({
				value: d.value,
				link: d.link,
			})),
			itemStyle: {
				color: function (param: { dataIndex: number }) {
					return chartColors[param.dataIndex % chartColors.length];
				},
			},
			label: {
				show: true,
				position: ["100%", "50%"],
				height: 25,
				width: 25,
				align: "center",
				verticalAlign: "middle",
				padding: 4,
				backgroundColor: "#fff",
				borderRadius: 25,
				borderColor: "inherit",
				borderWidth: 1,
				fontWeight: "bolder",
				fontSize: 12,
			},
			barMinWidth: 40,
			emphasis: {
				itemStyle: {
					shadowBlur: 10,
				},
			},
			universalTransition: {
				enabled: true,
				divideShape: "clone",
			},
		},
	};
	return option;
}

function lessonUsageLink(courseId: number, categoryId: number, lessonId: number) {
	return `/reports/course/usage/lesson?lessonId=${lessonId}&categoryId=${categoryId}&courseId=${courseId}`;
}

interface Props {
	className?: string;
	courseId: number;
	data: TutorialUsage[];
}

export function LessonUsageByCategoryChart(props: Props) {
	const navigate = useNavigate();

	const chartData = props.data.map((d) => ({
		name: d.lessonName,
		value: d.timeSpentMinutes,
		link: lessonUsageLink(props.courseId, d.tutorialCategoryId, d.lessonId),
	}));

	const options = initializeOptions(chartData);
	const chartHeight = props.data.length * 55 + 64;

	const onChartClick = (params: any, echarts: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	if (!props.data || props.data.length == 0) return "";

	return (
		<ReactEcharts
			option={options}
			className={`w-full pl-6 ${props.className}`}
			style={{
				height: chartHeight,
			}}
			onEvents={{
				click: onChartClick,
			}}
		/>
	);
}
